import React, { useRef, useEffect } from "react"
import { webrtcService } from "../services/api"

interface Props {
  rtspUrl: string
  name?: string
}

interface WebRTCAnswer {
  sdp: string
  type: RTCSdpType
}

const LiveView: React.FC<Props> = ({ rtspUrl }) => {

  const videoRef = useRef<HTMLVideoElement>(null)
  const pcRef = useRef<RTCPeerConnection | null>(null)
  const currentUrlRef = useRef<string | null>(null)

  useEffect(() => {

    if (!rtspUrl) return

    // 🚫 Prevent duplicate connection for same URL
    if (currentUrlRef.current === rtspUrl) {
      return
    }

    currentUrlRef.current = rtspUrl

    const startWebRTC = async () => {

      try {

        // 🔴 Close old connection safely
        if (pcRef.current) {
          pcRef.current.ontrack = null
          pcRef.current.oniceconnectionstatechange = null
          pcRef.current.close()
          pcRef.current = null
        }

        // ✅ Create PeerConnection with STUN
        const pc = new RTCPeerConnection({
          iceServers: [
            { urls: "stun:stun.l.google.com:19302" }
          ]
        })

        pcRef.current = pc

        pc.oniceconnectionstatechange = () => {
          console.log(`[LiveView] ICE state: ${pc.iceConnectionState} | rtsp: ${rtspUrl}`)
          if (pc.iceConnectionState === "failed" || pc.iceConnectionState === "closed") {
            console.error("[LiveView] ICE failed — check STUN/TURN config and HTTPS")
            pc.close()
          }
        }

        pc.onicegatheringstatechange = () => {
          console.log(`[LiveView] ICE gathering: ${pc.iceGatheringState}`)
        }

        pc.onicecandidate = (e) => {
          if (e.candidate) console.log("[LiveView] ICE candidate:", e.candidate.type, e.candidate.address)
          else console.log("[LiveView] ICE gathering complete")
        }

        pc.onconnectionstatechange = () => {
          console.log(`[LiveView] Connection state: ${pc.connectionState}`)
        }

        // ✅ Add video transceiver BEFORE offer
        pc.addTransceiver("video", { direction: "recvonly" })

        // ✅ Receive stream
        pc.ontrack = (event) => {
          if (videoRef.current) {
            videoRef.current.srcObject = event.streams[0]
          }
        }

        // ✅ Create Offer
        const offer = await pc.createOffer()
        await pc.setLocalDescription(offer)

        // ✅ Send to backend
        const answer: WebRTCAnswer = await webrtcService.createOffer({
          sdp: offer.sdp!,
          type: offer.type,
          rtsp_url: rtspUrl
        })

        // 🛑 Ensure pc still exists before applying answer
        if (!pcRef.current) return

        // ✅ Apply Answer
        await pc.setRemoteDescription({
          sdp: answer.sdp,
          type: answer.type
        })

        console.log("✅ WebRTC connected:", rtspUrl)

      } catch (err) {
        console.error("❌ WebRTC failed:", err)
      }
    }

    startWebRTC()

    // ✅ Cleanup when component unmounts
    return () => {
      if (pcRef.current) {
        pcRef.current.close()
        pcRef.current = null
      }
      currentUrlRef.current = null
    }

  }, [rtspUrl])

  return (
    <video
      ref={videoRef}
      autoPlay
      playsInline
      muted
      controls={false}
      style={{
        width: "100%",
        height: "100%",
        background: "#000"
      }}
    />
  )
}

export default LiveView